import React, { useEffect, useState } from 'react';
import { getCobradorDashboard, marcarVisita, createPago } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
    MapPin, Phone, DollarSign, Clock, CheckCircle, 
    AlertTriangle, User, Calendar, Navigation, CreditCard
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 2,
    }).format(value);
};

const prioridadColors = {
    alta: 'bg-destructive/10 text-destructive border-destructive/20',
    media: 'bg-warning/10 text-warning border-warning/20',
    normal: 'bg-accent/10 text-accent border-accent/20',
};

export default function CobradorDashboardPage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [pagoDialogOpen, setPagoDialogOpen] = useState(false);
    const [visitaDialogOpen, setVisitaDialogOpen] = useState(false);
    const [pagoForm, setPagoForm] = useState({ monto: '', forma_pago: 'efectivo' });
    const [visitaForm, setVisitaForm] = useState({ resultado: '', notas: '' });

    useEffect(() => {
        loadDashboard();
    }, []);

    const loadDashboard = async () => {
        try {
            const { data: dashboardData } = await getCobradorDashboard();
            setData(dashboardData);
        } catch (error) {
            toast.error('Error al cargar dashboard');
        } finally {
            setLoading(false);
        }
    };

    const handleRegistrarPago = async () => {
        if (!selectedCliente || !pagoForm.monto) return;
        try {
            await createPago({
                prestamo_id: selectedCliente.prestamo_id,
                monto: parseFloat(pagoForm.monto),
                forma_pago: pagoForm.forma_pago,
                notas: `Cobro en campo - ${selectedCliente.cliente_nombre}`
            });
            toast.success('Pago registrado exitosamente');
            setPagoDialogOpen(false);
            setPagoForm({ monto: '', forma_pago: 'efectivo' });
            loadDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al registrar pago');
        }
    };

    const handleMarcarVisita = async () => {
        if (!selectedCliente || !visitaForm.resultado) return;
        try {
            await marcarVisita({
                cliente_id: selectedCliente.cliente_id,
                prestamo_id: selectedCliente.prestamo_id,
                resultado: visitaForm.resultado,
                notas: visitaForm.notas
            });
            toast.success('Visita registrada');
            setVisitaDialogOpen(false);
            setVisitaForm({ resultado: '', notas: '' });
            loadDashboard();
        } catch (error) {
            toast.error('Error al registrar visita');
        }
    };

    const openPagoDialog = (cliente) => {
        setSelectedCliente(cliente);
        setPagoForm({ monto: cliente.total_pagar.toString(), forma_pago: 'efectivo' });
        setPagoDialogOpen(true);
    };

    const openVisitaDialog = (cliente) => {
        setSelectedCliente(cliente);
        setVisitaDialogOpen(true);
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
                </div>
                <Skeleton className="h-96" />
            </div>
        );
    }

    if (!data?.ruta && user?.rol === 'cobrador') {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <Card className="max-w-md text-center p-8">
                    <MapPin className="w-16 h-16 mx-auto text-muted-foreground/30 mb-4" />
                    <h2 className="font-heading text-xl font-bold mb-2">Sin Ruta Asignada</h2>
                    <p className="text-muted-foreground">
                        No tienes una ruta de cobro asignada. Contacta al administrador.
                    </p>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="cobrador-dashboard">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold">
                        {user?.rol === 'cobrador' ? 'Mi Ruta de Hoy' : 'Dashboard de Cobranza'}
                    </h1>
                    <p className="text-muted-foreground mt-1 flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        {data?.fecha}
                        {data?.ruta && (
                            <>
                                <span className="mx-2">•</span>
                                <MapPin className="w-4 h-4" />
                                {data.ruta.nombre} - {data.ruta.zona}
                            </>
                        )}
                    </p>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="animate-fadeIn">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                                <DollarSign className="w-5 h-5 text-primary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Por Cobrar</p>
                                <p className="text-xl font-bold font-mono">{formatCurrency(data?.resumen?.por_cobrar || 0)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="animate-fadeIn stagger-1">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-accent/10">
                                <CheckCircle className="w-5 h-5 text-accent" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Cobrado Hoy</p>
                                <p className="text-xl font-bold font-mono text-accent">{formatCurrency(data?.resumen?.cobrado_hoy || 0)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="animate-fadeIn stagger-2">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-warning/10">
                                <Clock className="w-5 h-5 text-warning" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Pendiente</p>
                                <p className="text-xl font-bold font-mono">{formatCurrency(data?.resumen?.pendiente || 0)}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card className="animate-fadeIn stagger-3">
                    <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-secondary/10">
                                <User className="w-5 h-5 text-secondary" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Clientes</p>
                                <p className="text-xl font-bold">{data?.total_clientes_hoy || 0}</p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Estadísticas del mes */}
            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="font-heading text-lg">Estadísticas del Mes</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                            <p className="text-sm text-muted-foreground">Total Cobrado</p>
                            <p className="text-2xl font-heading font-bold text-primary">
                                {formatCurrency(data?.estadisticas_mes?.total_cobrado || 0)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">Pagos Realizados</p>
                            <p className="text-2xl font-heading font-bold">
                                {data?.estadisticas_mes?.cantidad_pagos || 0}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-muted-foreground">Promedio Diario</p>
                            <p className="text-2xl font-heading font-bold text-accent">
                                {formatCurrency(data?.estadisticas_mes?.promedio_diario || 0)}
                            </p>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Lista de clientes a visitar */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg">Clientes a Visitar Hoy</CardTitle>
                    <CardDescription>
                        Ordenados por prioridad - Primero los más atrasados
                    </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                    {data?.clientes_hoy?.length === 0 ? (
                        <div className="text-center py-12">
                            <CheckCircle className="w-16 h-16 mx-auto text-accent/30 mb-4" />
                            <p className="text-muted-foreground">¡No hay clientes pendientes hoy!</p>
                        </div>
                    ) : (
                        <div className="divide-y">
                            {data?.clientes_hoy?.map((cliente, index) => (
                                <div 
                                    key={`${cliente.prestamo_id}-${cliente.cuota_numero}`}
                                    className="p-4 hover:bg-muted/50 transition-colors animate-fadeIn"
                                    style={{ animationDelay: `${index * 0.05}s` }}
                                >
                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="font-medium">{cliente.cliente_nombre}</h3>
                                                <Badge variant="outline" className={prioridadColors[cliente.prioridad]}>
                                                    {cliente.dias_atraso > 0 ? `${cliente.dias_atraso} días` : 'Hoy'}
                                                </Badge>
                                            </div>
                                            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                                                <span className="flex items-center gap-1">
                                                    <Phone className="w-3 h-3" />
                                                    {cliente.cliente_telefono}
                                                </span>
                                                <span className="flex items-center gap-1">
                                                    <MapPin className="w-3 h-3" />
                                                    {cliente.cliente_direccion}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="text-right">
                                                <p className="text-sm text-muted-foreground">Cuota #{cliente.cuota_numero}</p>
                                                <p className="font-mono font-bold text-lg">{formatCurrency(cliente.total_pagar)}</p>
                                                {cliente.mora > 0 && (
                                                    <p className="text-xs text-destructive">
                                                        Incluye mora: {formatCurrency(cliente.mora)}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    onClick={() => openPagoDialog(cliente)}
                                                    data-testid={`cobrar-${cliente.prestamo_id}`}
                                                >
                                                    <CreditCard className="w-4 h-4 mr-1" />
                                                    Cobrar
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => openVisitaDialog(cliente)}
                                                >
                                                    <AlertTriangle className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Dialog de Pago */}
            <Dialog open={pagoDialogOpen} onOpenChange={setPagoDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Registrar Pago</DialogTitle>
                    </DialogHeader>
                    {selectedCliente && (
                        <div className="space-y-4">
                            <Card className="bg-muted/50">
                                <CardContent className="p-4">
                                    <p className="font-medium">{selectedCliente.cliente_nombre}</p>
                                    <p className="text-sm text-muted-foreground">
                                        Cuota #{selectedCliente.cuota_numero} - Vence: {selectedCliente.fecha_vencimiento}
                                    </p>
                                    <p className="text-lg font-mono font-bold text-primary mt-2">
                                        Total: {formatCurrency(selectedCliente.total_pagar)}
                                    </p>
                                </CardContent>
                            </Card>
                            <div className="space-y-2">
                                <Label>Monto a Cobrar</Label>
                                <Input
                                    type="number"
                                    value={pagoForm.monto}
                                    onChange={(e) => setPagoForm({ ...pagoForm, monto: e.target.value })}
                                    min="1"
                                    step="0.01"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Forma de Pago</Label>
                                <Select
                                    value={pagoForm.forma_pago}
                                    onValueChange={(value) => setPagoForm({ ...pagoForm, forma_pago: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="efectivo">Efectivo</SelectItem>
                                        <SelectItem value="transferencia">Transferencia</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex justify-end gap-2 pt-4">
                                <Button variant="outline" onClick={() => setPagoDialogOpen(false)}>
                                    Cancelar
                                </Button>
                                <Button onClick={handleRegistrarPago}>
                                    Registrar Pago
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            {/* Dialog de Visita */}
            <Dialog open={visitaDialogOpen} onOpenChange={setVisitaDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Registrar Visita</DialogTitle>
                    </DialogHeader>
                    {selectedCliente && (
                        <div className="space-y-4">
                            <p className="text-muted-foreground">
                                Cliente: <strong>{selectedCliente.cliente_nombre}</strong>
                            </p>
                            <div className="space-y-2">
                                <Label>Resultado de la Visita</Label>
                                <Select
                                    value={visitaForm.resultado}
                                    onValueChange={(value) => setVisitaForm({ ...visitaForm, resultado: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Seleccionar resultado" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="no_encontrado">No Encontrado</SelectItem>
                                        <SelectItem value="promesa_pago">Promesa de Pago</SelectItem>
                                        <SelectItem value="rechazado">Rechazó Pagar</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Notas</Label>
                                <Input
                                    value={visitaForm.notas}
                                    onChange={(e) => setVisitaForm({ ...visitaForm, notas: e.target.value })}
                                    placeholder="Observaciones..."
                                />
                            </div>
                            <div className="flex justify-end gap-2 pt-4">
                                <Button variant="outline" onClick={() => setVisitaDialogOpen(false)}>
                                    Cancelar
                                </Button>
                                <Button onClick={handleMarcarVisita} disabled={!visitaForm.resultado}>
                                    Registrar
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
